const CONTACT_EMAIL = "drawing-conclusions-game@protonmail.com";

/** How many round trips one "Test Ping" press measures. Kept small and paired
 * with PING_COOLDOWN_MS below so a press is a brief burst, not a stream - the
 * server enforces its own per-IP allowance sized around these two numbers (see
 * the /api/ping route in server/app.ts). */
const PING_SAMPLE_COUNT = 3;
const PING_COOLDOWN_MS = 5_000;
const PING_TIMEOUT_MS = 5_000;

/** Times one round trip to /api/ping, or null if it failed/timed out. */
async function takePingSample(): Promise<number | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
  const start = performance.now();
  try {
    // Unique query param + no-store: without both, a browser or intermediary
    // cache can answer instantly and we'd report a fantasy latency of ~0ms.
    const res = await fetch(`/api/ping?t=${Date.now()}`, { cache: "no-store", signal: controller.signal });
    if (!res.ok) return null;
    await res.arrayBuffer();
    return performance.now() - start;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export function initHowToPlay(): void {
  const overlay = document.getElementById("how-to-play-overlay")!;
  const modal = document.getElementById("how-to-play-modal")!;
  const openBtn = document.getElementById("how-to-play-btn") as HTMLButtonElement;

  // Persists across open/close of the modal, so closing and reopening it isn't
  // a way to skip the cooldown.
  let pingCooldownUntil = 0;

  function closeOverlay(): void {
    overlay.classList.add("hidden");
  }

  openBtn.addEventListener("click", () => {
    overlay.classList.remove("hidden");
    render();
  });
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeOverlay();
  });

  function buildSection(title: string, topLevel = false): HTMLElement {
    const section = document.createElement("div");
    section.className = topLevel ? "how-to-play-section how-to-play-section--top" : "how-to-play-section";
    const heading = document.createElement(topLevel ? "h2" : "h3");
    heading.textContent = title;
    section.appendChild(heading);
    return section;
  }

  /** Paragraph builder with **bold** spans - built as real text nodes rather
   * than innerHTML so the copy stays readable in source without ever routing
   * through HTML parsing. */
  function paragraph(text: string): HTMLElement {
    const p = document.createElement("p");
    for (const [i, part] of text.split("**").entries()) {
      if (part === "") continue;
      if (i % 2 === 1) {
        const strong = document.createElement("strong");
        strong.textContent = part;
        p.appendChild(strong);
      } else {
        p.appendChild(document.createTextNode(part));
      }
    }
    return p;
  }

  function buildPingControl(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "how-to-play-ping";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "secondary-button";
    button.textContent = "Test Ping";

    const result = document.createElement("span");
    result.className = "how-to-play-ping-result";

    let cooldownTimer: ReturnType<typeof setInterval> | null = null;

    /** Drives the button off the already-set `pingCooldownUntil` deadline.
     * Deliberately does NOT set that deadline itself: this also runs when a
     * mid-cooldown modal rebuild re-attaches to an in-flight cooldown, and
     * pushing the deadline out there would let closing and reopening the modal
     * repeatedly restart the full wait instead of resuming what's left of it. */
    function attachCooldownCountdown(): void {
      if (cooldownTimer) clearInterval(cooldownTimer);
      const tick = (): void => {
        const remainingMs = pingCooldownUntil - Date.now();
        if (remainingMs <= 0) {
          if (cooldownTimer) clearInterval(cooldownTimer);
          cooldownTimer = null;
          button.disabled = false;
          button.textContent = "Test Ping";
          return;
        }
        button.disabled = true;
        button.textContent = `Test Ping (${Math.ceil(remainingMs / 1000)}s)`;
      };
      tick();
      cooldownTimer = setInterval(tick, 250);
    }

    function beginCooldown(): void {
      pingCooldownUntil = Date.now() + PING_COOLDOWN_MS;
      attachCooldownCountdown();
    }

    button.addEventListener("click", async () => {
      if (Date.now() < pingCooldownUntil) return;
      button.disabled = true;
      button.textContent = "Pinging...";
      result.textContent = "";
      result.classList.remove("how-to-play-ping-result--error");

      let best: number | null = null;
      for (let i = 0; i < PING_SAMPLE_COUNT; i++) {
        const sample = await takePingSample();
        // Keep the fastest of the samples, same reasoning as clockSync.ts: the
        // quickest round trip is the one least distorted by a momentarily busy
        // network or event loop, so it's the fairest read on the actual link.
        if (sample !== null && (best === null || sample < best)) best = sample;
      }

      if (best === null) {
        result.textContent = "Couldn't reach the server - please try again.";
        result.classList.add("how-to-play-ping-result--error");
      } else {
        result.textContent = `${Math.round(best)} ms`;
      }
      beginCooldown();
    });

    // Reopening the modal mid-cooldown rebuilds this control, so re-attach the
    // countdown rather than handing back a freshly enabled button.
    if (Date.now() < pingCooldownUntil) attachCooldownCountdown();

    wrap.append(button, result);
    return wrap;
  }

  function render(): void {
    modal.innerHTML = "";

    const header = document.createElement("div");
    header.className = "settings-header";
    const title = document.createElement("h2");
    title.textContent = "How to Play";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "close-button";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", () => closeOverlay());
    header.append(title, closeBtn);
    modal.appendChild(header);

    const body = document.createElement("div");
    body.className = "how-to-play-body";

    const basics = buildSection("The Basics");
    basics.appendChild(
      paragraph(
        "Players take turns as the artist and choose one of three words to draw, while everyone " +
          "else tries to guess the word in chat."
      )
    );
    basics.appendChild(
      paragraph(
        "The artist gets points for every player who guesses correctly, with harder words giving a " +
          "higher point multiplier."
      )
    );
    basics.appendChild(
      paragraph(
        "Players get points for guessing correctly too. **The faster you guess, the more points you get!**"
      )
    );
    basics.appendChild(paragraph("But wait, there's more..."));
    body.appendChild(basics);

    const twists = buildSection("Chaos Canvas Cards and Achievements");
    twists.appendChild(
      paragraph(
        "To make things a little more interesting, **Chaos Canvas Cards (CCCs)** and **Achievements** " +
          "are enabled by default."
      )
    );
    twists.appendChild(
      paragraph(
        "CCCs are a double-edged sword. They increase your point multiplier as the artist, but also " +
          "make drawing more challenging. You can upgrade, discard, or stack them, but that tempting " +
          "multiplier might not always be worth it."
      )
    );
    twists.appendChild(
      paragraph("Achievements, on the other hand, add a little twist to the final score reveal.")
    );
    body.appendChild(twists);

    const about = buildSection("About This Game", true);
    about.appendChild(
      paragraph(
        "**Drawing Conclusions** is a simple hobby project made by a single developer and hosted on a " +
          "little server in the Netherlands."
      )
    );
    about.appendChild(
      paragraph(
        "It started as a fun, free online game for me and my friends because I wasn't really happy with " +
          "the alternatives out there. It's mostly vibe coded, and I'm still adding features, " +
          "improvements, and bug fixes whenever I get the chance."
      )
    );
    about.appendChild(
      paragraph(
        "That said, please keep in mind that I can't guarantee 100% uptime or flawless performance and ping."
      )
    );
    about.appendChild(paragraph("Speaking of ping, you can test yours by clicking the button below."));
    about.appendChild(buildPingControl());

    const contact = document.createElement("p");
    contact.append(document.createTextNode("If you have any suggestions, find a bug, or run into any issues, feel free to contact me at "));
    const mailLink = document.createElement("a");
    mailLink.className = "how-to-play-inline-link";
    mailLink.href = `mailto:${CONTACT_EMAIL}`;
    mailLink.textContent = CONTACT_EMAIL;
    contact.append(mailLink, document.createTextNode("."));
    about.appendChild(contact);

    const glossaryPara = document.createElement("p");
    glossaryPara.append(document.createTextNode("For a detailed breakdown of all settings, functions and achievements, visit the "));
    const glossaryLink = document.createElement("a");
    glossaryLink.className = "how-to-play-inline-link";
    glossaryLink.href = "/glossary.html";
    glossaryLink.target = "_blank";
    glossaryLink.rel = "noopener";
    glossaryLink.textContent = "Glossary";
    glossaryPara.append(glossaryLink, document.createTextNode("."));
    about.appendChild(glossaryPara);
    body.appendChild(about);

    modal.appendChild(body);
  }
}
